import React from 'react';
import PropTypes from 'prop-types';

import {
  actions,
  BetItemProvider,
  BetManager,
  isNotEmptyObject,
  Logger,
  useApplicationState,
  useBetItem,
  useBetslipDispatch,
  useBetslipState,
} from '@atlas/core';
import { EventLink } from '@atlas/routing';
import { BetCard, currencySymbols } from '@atlas/ui';

import MoneyBlock from './MoneyBlock';

export default function BetItemsList() {
  const { betItems, bonuses, outcomesErrors } = useBetslipState();
  const {
    account: { currency },
    authenticated,
  } = useApplicationState();

  const BetItemComponents = React.useMemo(
    () =>
      betItems.map(betItem => (
        <BetItemProvider betItem={betItem} key={betItem.requestId}>
          <BetCard EventLink={EventLink} invalid={outcomesErrors[betItem.outcomeId]} />
          <BetCardInput
            amount={betItem.amount}
            authenticated={authenticated}
            bonuses={bonuses[betItem.requestId]}
            currency={currency}
          />
        </BetItemProvider>
      )),
    [outcomesErrors, betItems, bonuses, authenticated, currency],
  );

  return BetItemComponents;
}

const BetCardInput = props => {
  const { amount, authenticated, bonuses, currency } = props;

  const betItemState = useBetItem();

  const { eventId, outcome, outcomeId, requestId } = betItemState;

  const dispatch = useBetslipDispatch();

  const maxBetButtonParams = React.useMemo(() => {
    const result = {};

    const metadata = outcome?.metadata;

    // FIXME: Temperary fix for Longshot
    if (isNotEmptyObject(metadata)) {
      // Applying max bet value to button params
      const maxStakeByCurrencyMap = metadata.feed?.maxStakeByCurrency;
      if (typeof maxStakeByCurrencyMap === 'object') {
        const value = maxStakeByCurrencyMap[currency];
        const numberValue = Number(value);

        if (!Number.isNaN(numberValue) && numberValue > 0) {
          result.currency = currency;
          result.currencySymbol = currencySymbols[currency] || currency;
          result.value = numberValue;
        }
      }
    }

    return result;
  }, [currency, outcome]);

  const handleBetItemAmountChange = React.useCallback(
    amount =>
      dispatch({
        payload: {
          amount,
          eventId,
          outcomeId,
          requestId,
        },
        type: actions.BETSLIP_UPDATE_BET,
      }),
    [outcomeId, eventId, requestId, dispatch],
  );

  // FIXME: Temp solution
  const [maxBetLoading, setMaxBetLoading] = React.useState(false);

  const handleGetMaxBet = React.useCallback(() => {
    if (maxBetButtonParams.value) {
      return handleBetItemAmountChange(maxBetButtonParams.value);
    }

    setMaxBetLoading(true);

    BetManager.getMaxBet(betItemState)
      .then(limits => {
        handleBetItemAmountChange(limits.finalMaxBetLimit);
      })
      .catch(error => {
        Logger.info(`[bet:${betItemState.outcomeId}]`, 'unable to resolve max bet', error);
      })
      .finally(() => {
        setMaxBetLoading(false);
      });
  }, [maxBetButtonParams, handleBetItemAmountChange, betItemState, setMaxBetLoading]);

  return (
    <MoneyBlock
      authenticated={authenticated}
      betAmount={amount}
      bonuses={bonuses}
      currency={currency}
      isActive={betItemState.active}
      loading={maxBetLoading}
      maxBetParams={maxBetButtonParams}
      onMaxBetClick={handleGetMaxBet}
      selectedBet={betItemState}
      setBetAmount={handleBetItemAmountChange}
    />
  );
};

BetCardInput.propTypes = {
  amount: PropTypes.number.isRequired,
  authenticated: PropTypes.bool.isRequired,
  bonuses: PropTypes.array.isRequired,
  currency: PropTypes.string.isRequired,
};
